"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { Auction, BiddingSchedule, BiddingScheduleRange, HighestBid, Lot, AuctionState } from "@prisma/client";
import useSWR, { useSWRConfig } from "swr";
import { Flex, Text } from "@radix-ui/themes";
import { useAuctionTime } from "@/lib/context/AuctionTimeProvider";
import { useSharedSubscription } from "@/app/hooks/useSharedNATS";
import { Skeleton } from "@/app/components/ui/skeleton";
import { cn, fetcher } from "@/lib/utils";
import HybridLiveLotPanel from "./HybridLiveLotPanel";
import { LiveCloseProvider } from "@/app/admin/auctions/[auctionId]/live-close/LiveCloseContext";
// Dynamically import to avoid SSR evaluating browser-only SDKs inside IVSViewer
const IvsViewer = dynamic(
  () => import("@/app/admin/auctions/[auctionId]/live-close/widgets/IVSViewer"),
  { ssr: false }
);
import LotPageImagePreview from "../lots/[lotId]/LotPageImagePreview";
import { useOneTimeNotification } from "@/app/hooks/useOneTimeNotification";
import HybridInfoDialog from "../HybridInfoDialog";
import { LOT_BID_HISTORY_MAX_BID_QUERY_KEY, LOT_BID_HISTORY_QUERY_KEY } from '@/app/auctions/[auctionId]/lots/[lotId]/history/LotBidHistory';

interface Props {
  auction: Auction;
  lots: (Lot & { HighestBid: HighestBid[] })[];
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
}

const HybridLiveWidget = ({ lots, auction, biddingSchedule }: Props) => {
  const { mutate } = useSWRConfig();
  const { currentTime } = useAuctionTime();

  // Sort lots by id for consistent navigation
  const lotsById = useMemo(() => [...lots].sort((a, b) => a.id - b.id), [lots]);

  // Live lots from SWR (fallback to initial lots)
  const { data: lotsData, isLoading: isLoadingLots, mutate: mutateLots } = useSWR<
    (Lot & { HighestBid?: HighestBid[] })[]
  >(
    `/api/auctions/${auction.id}/lots?valid=true&disabled=false`,
    fetcher,
    {
      fallbackData: lotsById,
      revalidateOnMount: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      keepPreviousData: true as any,
    }
  );

  // Start on the first lot that hasn't ended yet (fallback to first by id)
  const [activeIndex, setActiveIndex] = useState<number>(() => {
    const now = new Date();
    const idx = lotsData?.findIndex((lot) => new Date(lot.endDate) > now);
    return typeof idx === 'number' && idx >= 0 ? idx : -1;
  });

  useEffect(() => {
    const revalidate = () => mutateLots(); // re-fetch from server

    // Fires when tab becomes visible again
    const onVisibility = () => {
      if (document.visibilityState === "visible") revalidate();
    };

    // Fires when page is restored from BFCache (common on iOS)
    const onPageShow = (e: PageTransitionEvent) => {
      // You can revalidate unconditionally; `persisted` is a hint it came from BFCache
      revalidate();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [mutateLots]);

  const liveLotsById = useMemo(() => {
    const source = lotsData ?? lotsById;
    return [...source].sort((a, b) => a.catalogNumber - b.catalogNumber);
  }, [lotsData, lotsById]);

  const goToNextLot = useCallback(() => {
    setActiveIndex((prev) => prev + 1);
  }, []);

  const activeLot = (activeIndex >= 0 && activeIndex < liveLotsById.length) ? liveLotsById[activeIndex] : null;

  const finalLotId = useMemo(() => {
    return liveLotsById.length > 0 ? liveLotsById[liveLotsById.length - 1].id.toString() : '';
  }, [liveLotsById]);

  const onLotChange = useCallback(
    (payload: any) => {
      if (payload?.endDate && payload?.id) {
        mutateLots((existing) => {
          const base = existing ?? lotsById;
          return base.map((l) =>
            l.id === payload.id ? { ...l, endDate: new Date(payload.endDate) } as Lot : l
          );
        }, false);
      }
    },
    [mutateLots, lotsById]
  );

  useSharedSubscription({
    topic: `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.Auction${auction.id}.Lot`,
    onMessage: onLotChange,
  });

  const { data: currentHighestBid, mutate: mutateCurrentHighestBid } = useSWR(
    `/api/auctions/${auction.id}/lots/${activeLot?.id}/highestBid`,
    fetcher,
    {
      fallbackData: activeLot?.HighestBid?.[0] ?? null,
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  const onNewHighestBid = useCallback(
    (payload: any) => {
      if (!payload?.amount) return;
      mutateCurrentHighestBid(payload as HighestBid, false);
      mutate([LOT_BID_HISTORY_QUERY_KEY, auction.id.toString(), activeLot?.id?.toString()]);
      mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auction.id.toString(), activeLot?.id?.toString()]);
    },
    [mutateCurrentHighestBid, mutate, auction.id, activeLot?.id]
  );

  useSharedSubscription({
    topic: `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.Lot${activeLot?.id}.HighestBid`,
    onMessage: onNewHighestBid,
  });

  // --- Tabs: "video" | "images" ---
  const [activeTab, setActiveTab] = useState<"video" | "images">("video");
  // No ref needed; the viewer handles its own media element


  const handleSelectVideoTab = useCallback(() => {
    setActiveTab("video");
  }, []);

  const handleSelectImagesTab = useCallback(() => {
    setActiveTab("images");
  }, []);

  const { shouldShow: shouldShowHybridInfo } = useOneTimeNotification(
    `hybrid-auction-info`
  );
  const [hybridInfoOpen, setHybridInfoOpen] = useState(false);

  useEffect(() => {
    if (shouldShowHybridInfo) {
      setHybridInfoOpen(true);
    }
  }, [shouldShowHybridInfo]);

  // No automatic PiP handling; user controls PiP manually via the video element

  return (
    <>
      <HybridInfoDialog open={hybridInfoOpen} onOpenChange={setHybridInfoOpen} liveClose={true} />
      <Flex className="w-full">
        <Flex justify="center" className="w-full">
          <Flex direction="column" className="w-full">
            {/* Title */}
            <Flex className={
              cn("w-full lg:w-11/12 lg:-translate-x-4 mb-2 max-md:mb-4 mx-auto",
                !activeLot && "lg:translate-x-0")
            } justify="start">
              <Flex className="w-full rounded-lg" justify="between" align="center">
                <Text className="text-lg sm:text-xl font-semibold">{auction.title} - Live Close</Text>
                {!activeLot && (
                  <Flex className="rounded-full bg-red-100 px-4 py-1 ring-2 ring-red-400">
                    <Text className="text-xs sm:text-sm text-red-700 font-semibold">
                      Auction Complete
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>

            <Flex justify="center" className="w-full items-start flex-col lg:flex-row lg:space-x-8">
              {/* Left column: Tabs with IVS video or Images. Keep video element mounted for continuity + PiP */}
              <Flex direction="column" className={cn("w-full max-md:mb-5", activeLot ? "lg:w-7/12 lg:sticky lg:top-4" : "")}>
                {/* Tabs header (only when a lot is active) */}
                {activeLot && (
                  <Flex className="w-full border-b border-gray-200">
                    <button
                      className={`px-4 py-2 text-sm font-medium ${activeTab === 'video' ? 'text-magenta border-b-2 border-magenta' : 'text-gray-600 hover:text-gray-900'}`}
                      onClick={handleSelectVideoTab}
                    >
                      Video
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium ${activeTab === 'images' ? 'text-magenta border-b-2 border-magenta' : 'text-gray-600 hover:text-gray-900'}`}
                      onClick={handleSelectImagesTab}
                    >
                      Images
                    </button>
                  </Flex>
                )}

                {/* Single video element kept mounted. Visible on Video tab; offscreen on Images tab. */}
                <Flex className={cn(
                  "w-full mt-2",
                  !activeLot && "lg:w-11/12 mx-auto"
                  // (activeTab === 'video' || !activeLot) ? 'lg:w-11/12 mx-auto mt-2' : 'w-full mt-2',
                  // !activeLot && 'lg:w-full'
                )} direction="column">
                  <Flex className={cn(
                    (activeTab === 'video' || !activeLot) ?
                      'aspect-video w-full rounded-md bg-black overflow-hidden' :
                      'fixed top-0 left-0 w-[2px] h-[2px] overflow-hidden opacity-5 pointer-events-none'
                  )}>
                    <IvsViewer />
                  </Flex>
                  {(activeTab === 'video' || !activeLot) && (
                    <Flex direction="column" align="end" justify="end" className="mt-2">
                      <Text className="text-xs text-gray-500">
                        Video may be delayed by up to several seconds.
                      </Text>
                      <Text className="text-xs text-gray-500">
                        It is not recommended to lock your device during the close.
                      </Text>
                    </Flex>
                  )}
                </Flex>

                {/* Images container (render only when Images tab is active and a lot is active) */}
                {activeTab === 'images' && !!activeLot && (
                  <Flex
                    className={cn("w-full mt-4")}
                    justify="center"
                  >
                    <LotPageImagePreview
                      auctionId={auction.id.toString()}
                      lotId={activeLot?.id?.toString() ?? ""}
                      className="w-full"
                    />
                  </Flex>
                )}
              </Flex>

              {/* Right column: Lot panel (only when a lot is active) */}
              {activeLot && (
                <Flex className="w-full lg:w-1/3 lg:flex-none lg:shrink-0" justify="end">
                  {isLoadingLots ? (
                    <Skeleton className="w-full h-64" />
                  ) : (
                    <LiveCloseProvider auctionId={auction.id.toString()} lotId={activeLot?.id?.toString() ?? ""} auctionState={auction.auctionState} finalLotId={finalLotId}>
                      <HybridLiveLotPanel
                        lot={activeLot!}
                        highestBid={currentHighestBid}
                        expired={activeLot ? new Date(activeLot.endDate).getTime() < (currentTime ? new Date(currentTime).getTime() : Date.now()) : false}
                        biddingSchedule={biddingSchedule}
                        auctionStart={auction.startDate}
                        onNextLot={goToNextLot}
                      />
                    </LiveCloseProvider>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default HybridLiveWidget;


